import api from "@/interceptors";
import {
  IOrgMemberRes,
  IOrgRole,
  IOrgRoleInput,
  IOrgRolePermLevel,
} from "@/interfaces/org/role";
import {
  IDomainRecords,
  IOrgDetails,
  IOrgProfile,
  IOrgRoleMembers,
  IOrgAccessType,
  IOrgAccessOfferRequest,
  IOrgCreateRequest,
  IOrgMemberCreateRequest,
  IOrgUpdateInput,
  IOrgVerificationCreate,
  IOrgVerificationUpdate,
  IOrgAccessRequestResponse,
  IOrgAccessOfferResponse,
} from "@/interfaces/orgInterfaces";
import { IApiPromise } from "@/interfaces/responseData";
import { IUserRequest } from "@/interfaces/userInterfaces";
import { Invite, Org, UserRoute } from "../apiRoutes/routes";
import utilities from "@/utils/utilities";
import { IMemberInfo } from "@/interfaces/members/IMember";
import { statusCode } from "@/utils/constants";

export const mapDataInternalMembersList = (data) => {
  if (data && data.length > 0) {
    data = data.map((member) => {
      member = {
        ...member,
        user: {
          nameText: member.firstName + " " + member.lastName,
          title: member.title,
          email: member.email,
          imgSrc: "",
        } as IMemberInfo,
        status: member.isLocked
          ? statusCode.locked
          : member.isDeactivated
            ? statusCode.deactivated
            : statusCode.active,
        roles: member.roles || [],
        organization: member.org && member.org.orgNameShort,
        id: member.userId,
        mfaEnabled: member.mfaEnabled,
      };
      return member;
    });
  }
  return data;
};

export const postAddOrgDomain = async (domain: string) => {
  return await api.post(Org.domain, { domain });
};

export const postDeleteOrgDomain = async (domainId: string) => {
  return await api.delete(Org.getDomain.replace(":id", domainId));
};

export const deleteMembersOfRole = (
  roleId: string,
  payload,
): IApiPromise<IOrgRole> =>
  api.delete(Org.deleteMembersOfRole.replace(":roleId", roleId), {
    data: payload,
  });

export const getSwitchOrgs = (): IApiPromise<any[]> =>
  api.get(Org.getOrgsSwitch);

export const getOrgVerify = (payload: {
  includeArchived: boolean;
}): IApiPromise<any[]> => {
  return api.get(
    `${Org.getOrgVerify}${
      payload?.includeArchived ? "?includeArchived=true" : ""
    }`,
  );
};

export const postSwitchOrg = (orgId: string) =>
  api.post(Org.switchOrg, { orgId });

export const postSwitchToHomeOrg = () => api.post(Org.switchToHomeOrg);

export const postOrgAccessRequest = (payload: any) =>
  api.post(Invite.orgAccessRequest, payload);

export const orgAccessRequestProcess = (payload: any) =>
  api.post(Invite.orgAccessRequestProcess, payload);

export const getOrgProfile = (): IApiPromise<IOrgProfile> =>
  api.get(Org.getOrgProfile);

export const postOrgProfile = (data: { bio: string }) =>
  api.post(Org.postOrgProfile, data);

export const getOrgDetails = (orgId: string): IApiPromise<IOrgDetails> =>
  api.get(Org.getOrg.replace(":id", orgId));

export const getOrgRoles = (): IApiPromise<IOrgRole[]> =>
  api.get(Org.getOrgRoles);

export const fetchOrgRolesTypes = (): IApiPromise<IOrgRole[]> =>
  api.get(Org.getOrgRoleTypes);

export const fetchInvalidDomains = (): IApiPromise<string[]> =>
  api.get(Org.getOrgDomainsInvalid);

export const fetchRoleMembers = (
  roleId: number,
): IApiPromise<IOrgRoleMembers> =>
  api.get(Org.getMembersOfRole.replace(":id", roleId.toString()));

export const fetchOrgAccessType = (): IApiPromise<IOrgAccessType[]> =>
  api.get(Org.getOrgAccessTypes);

export const postOrgAccessOffer = (payload: IOrgAccessOfferRequest) =>
  api.post(Invite.orgAccessOffer, payload);

export const orgAccessOfferProcess = (payload) =>
  api.post(Invite.orgAccessOfferProcess, payload);

export const postOrgCreate = (payload: IOrgCreateRequest) =>
  api.post(Org.createOrg, payload);

export const postVerificationRequestOrgCreate = (
  payload: IOrgVerificationCreate,
) => api.post(Org.createRequestVerification, payload);

export const putVerificationRequestOrg = (
  orgId: string,
  payload: IOrgVerificationUpdate,
) =>
  api.put(Org.editRequestVerification.replace(":orgVerifyId", orgId), payload);

export const deleteOrgRequest = (payload: { orgVerifyIds: string[] }) =>
  api.delete(Org.deleteOrgRequest, { data: payload });

export const getOrgDomains = (orgId: string): IApiPromise<IDomainRecords> =>
  api.get(Org.getDomains.replace(":id", orgId));

export const addOrgMember = (
  orgMemberRequest: IUserRequest,
): IApiPromise<IUserRequest> => api.post(UserRoute.create, orgMemberRequest);

export const uploadProfileAvatar = (formData: FormData): IApiPromise<any> => {
  return api.post(Org.orgToProfileImageSave, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const uploadProfileLogo = (formData: FormData): IApiPromise<any> => {
  return api.post(Org.orgToProfileLogoSave, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const uploadAvatarUser = (
  formData: FormData,
  method,
): IApiPromise<any> => {
  return api[method](Org.orgUserUploadAvatar, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteAvatarUser = async (imageId: string): IApiPromise<any> =>
  await api.delete(
    Org.delOrgUserUploadAvatar.replace(":imageId", imageId.toString()),
  );

export const getOrgDomainsInvalid = async () => {
  return await api.get(Org.getOrgDomainsInvalid);
};

export const getOrgRoletypes = (): IApiPromise<any> =>
  api.get(Org.getOrgRoleTypes);

export const getOrgAccessGranted = (): IApiPromise<any> =>
  api.get(Org.getAccessGrantedToOtherOrgs);

export const getOrgMembersAddable = (orgId: string): IApiPromise<any> =>
  api.get(Org.getAddableOrgMembers.replace(":orgId", orgId));

export const postOrgDomainVerifyInitiate = async (orgDomainId: number) => {
  return await api.post(Org.orgDomainVerifyInitiate, { orgDomainId });
};

export const postOrgDomainVerify = async (orgDomainId: number) => {
  return await api.post(Org.orgDomainVerifyCheck, { orgDomainId });
};

export const postOrgDomainVerifyCallback = async (payload) => {
  return await api.post(
    Org.orgDomainVerifyEmailCallback,
    {
      token: payload.token,
    },
    {
      params: { "g-recaptcha-response": payload.recaptcha },
    } as any,
  );
};
export const postOrgDomainVerifyById = async (orgDomainId: number, email) => {
  return await api.post(
    Org.orgDomainVerify.replace(":domainId", orgDomainId.toString()),
    { email },
  );
};

export const getExternalMembersOrg = (): IApiPromise<any> =>
  api.get(`${Org.getExternalMembersOfOrg}?includeArchived=true`);

export const getInternalMembersOrg = (): IApiPromise<any> =>
  api.get(`${Org.getMembersOfOrg}?includeArchived=true`);

export const deactivateOrgMember = async (userId: string): IApiPromise<any> =>
  await api.post(Org.deactivateOrgMember.replace(":userId", userId));

export const reactivateOrgMember = async (userId: string): IApiPromise<any> =>
  await api.post(Org.reactivateOrgMember.replace(":userId", userId));

export const getOrgAccessGrantedToOther = async (): IApiPromise<any> =>
  await api.get(Org.getAccessGrantedToOtherOrgs);

export const postOrgMemberCreate = (payload: IOrgMemberCreateRequest) =>
  api.post(Org.createMember, payload);

export const getOrgRoleById = (id: string): IApiPromise<IOrgRole> =>
  api.get(Org.getOrgRoleById.replace(":id", id));

export const putOrgRole = (id: string, payload): IApiPromise<IOrgRole> =>
  api.put(Org.getOrgRoleById.replace(":id", id), payload);

export const deleteOrgMember = async (userId: string): IApiPromise<any> =>
  await api.delete(Org.deleteOrgMember.replace(":userId", userId));

export const deleteRole = async (roleId: string): IApiPromise<IOrgRole> =>
  await api.delete(Org.deleteRole.replace(":roleId", roleId));

export const resetMFAOrgMember = async (userId: string): IApiPromise<any> =>
  await api.post(Org.postOrgMemberMfaEnable.replace(":userId", userId));

export const resetPassOrgMember = async (
  userId: string,
  recaptcha: any,
): IApiPromise<any> =>
  await api.post(
    Org.resetPasswordOrgExternalMember.replace(":userId", userId),
    {},
    {
      params: { "g-recaptcha-response": recaptcha },
    } as any,
  );
export const unlockOrgMember = async (userId: string): IApiPromise<any> =>
  await api.post(Org.postOrgMemberUnlock.replace(":userId", userId));

export const getMemberOfRoles = (
  roleId: string,
): IApiPromise<IOrgMemberRes[]> =>
  api.get(Org.getMembersOfRole.replace(":id", roleId));

export const putMemberRoles = async (
  userId: string,
  roleIds: number[],
): IApiPromise<any> =>
  await api.put(
    utilities.getProcessedRoute(Org.putMemberRoles, [
      { key: ":userId", value: userId },
    ]),
    { roleIds: roleIds },
  );

export const deleteProfileAvatar = async (imageId: string): IApiPromise<any> =>
  await api.delete(`/org/profile/avatar?imageId=${imageId}`);

export const deleteProfileLogo = async (imageId: string): IApiPromise<any> =>
  await api.delete(
    Org.orgToProfileLogoDel.replace(":imageId", imageId.toString()),
  );

export const getProfileImage = async (
  imageId: string,
  orgId?: string,
): Promise<any> => {
  if (orgId) return await api.get(`/org/${orgId}/profile/image/${imageId}`);
  return await api.get(`/org/profile/image/${imageId}`);
};

export const putMember = async (
  userId: string,
  payload: any,
): IApiPromise<any> =>
  await api.put(
    utilities.getProcessedRoute(Org.putMember, [
      { key: ":userId", value: userId },
    ]),
    payload,
  );

export const resetPasswordOrgMember = async (
  userId: string,
  recaptcha: any,
): IApiPromise<any> =>
  await api.post(Org.resetPasswordOrgMember.replace(":userId", userId), {}, {
    params: { "g-recaptcha-response": recaptcha },
  } as any);

export const postOrgRole = (payload: IOrgRoleInput): IApiPromise<any> =>
  api.post(Org.postOrgRole, payload);

export const getOrgAccessGrantedByOther = (): IApiPromise<any[]> =>
  api.get(Org.getAccessGrantedToOtherOrgs);

export const getOrgAccessGrantedByCurrent = (): IApiPromise<any[]> =>
  api.get(Org.getAccessGrantedToCurrentOrg);

export const deleteAccessGrant = (payload: any): IApiPromise<any> =>
  api.delete(Org.deleteAccessGrant, { data: payload });

export const getAccessGrants = (): IApiPromise<any> =>
  api.get(Org.getAccessGrants);

export const deleteOrgExternalMember = async (
  userId: string,
): IApiPromise<any> =>
  await api.delete(Org.deleteOrgExternalMember.replace(":userId", userId));

export const putOrgProfile = (payload: IOrgUpdateInput): IApiPromise<any> =>
  api.put(Org.putOrgProfile, payload);

export const leaveOrg = async (orgId: string): IApiPromise<any> =>
  await api.delete(Org.leaveOrg.replace(":orgId", orgId));
export const getOrgs = (): IApiPromise<any[]> => api.get(Org.getOrgs);

export const getOrgPermLevels = (): IApiPromise<IOrgRolePermLevel[]> =>
  api.get(Org.getOrgPermLevels);

export const posthRoleMembers = (roleId: any, payload: any): IApiPromise<any> =>
  api.post(Org.getMembersOfRole.replace(":id", roleId), payload);

export const getCurrentUser = (): IApiPromise<any> => api.get(UserRoute.get);

export const updateUserProfile = (payload: any): IApiPromise<any> =>
  api.put(UserRoute.profileUpdate, payload);

export const getCurrentOrg = (): IApiPromise<any> =>
  api.get(Org.getOrgsCurrent);

export const setCurrentUser = (userId: string): IApiPromise<any> =>
  api.get(UserRoute.getCurrent.replace(":userId", userId));

export const getParentOrg = (orgId: string): IApiPromise<any> =>
  api.get(Org.getParentOrg.replace(":orgId", orgId));
export const getOrgIdProfile = (orgId: any): IApiPromise<any> =>
  api.get(Org.getOrgIdProfile.replace(":orgId", orgId));
export const getRoleByUserId = (userId: any): IApiPromise<any> =>
  api.get(Org.getRoleByUserId.replace(":userId", userId));

export const getPermissionLevelDetailsOfRoleType = (
  roleId: string,
): IApiPromise<any> =>
  api.get(Org.getPermissionLevelDetailsOfRoleType.replace(":roleId", roleId));

export const resetPasswordOrgMembers = async (
  members: number[],
): IApiPromise<any> => await api.post(Org.resetPasswordOrgMembers, { members });

export const resetMfaOrgMembers = async (members: number[]): IApiPromise<any> =>
  await api.post(Org.postOrgMembersMfaReset, { members });

export const resetMfaPasskeyOrgMembers = async (): IApiPromise<any> =>
  await api.delete(Org.deleteOrgMembersMfaResetPasskey, {});

export const postOrgMembersDeactivate = async (
  members: number[],
): IApiPromise<any> => await api.put(Org.putOrgMembersDeactivate, { members });

export const putOrgMembersActivate = async (
  members: number[],
): IApiPromise<any> => await api.put(Org.putOrgMembersActivate, { members });

export const deleteOrgMembersDelete = async (
  members: number[],
): IApiPromise<any> =>
  await api.delete(Org.deleteOrgMembersDelete, { data: { members } });

export const deleteOneOrgMemberExternalDelete = async (
  memberId: number,
): IApiPromise<any> =>
  await api.delete(
    Org.deleteOneOrgMemberExternalDelete.replace(
      ":memberId",
      memberId.toString(),
    ),
  );

export const deleteOrgMembersExternalDelete = async (
  members: number[],
): IApiPromise<any> =>
  await api.delete(Org.deleteOrgMembersExternalDelete, { data: { members } });

export const deleteOrgDomains = async (domainIds: number[]): IApiPromise<any> =>
  await api.delete(Org.deleteOrgDomains, { data: { domainIds } });

export const unVerifyDomain = async (orgDomainId: number[]): IApiPromise<any> =>
  await api.post(Org.unVerifyDomain, { orgDomainId });

export const unVerifyDomainEmail = async (
  orgDomainId: number[],
): IApiPromise<any> =>
  await api.post(Org.unVerifyDomainEmail, { domainIds: orgDomainId });

export const deleteOrgAccessGrants = async (
  grantIds: number[],
): IApiPromise<any> =>
  await api.delete(Org.deleteOrgAccessGrants, { data: { grantIds } });

export const postOrgVerifiCallback = (payload): IApiPromise<any> =>
  api.post(
    Org.postOrgVerifiCallback,
    {
      secretKey: payload.secretKey,
      token: payload.token,
    },
    {
      params: { "g-recaptcha-response": payload.recaptcha },
    } as any,
  );

export const verifyOtherUser = async (
  userId: string,
  payload,
): IApiPromise<any> =>
  await api.post(
    Org.postOrgMemberVerify.replace(":userId", userId.toString()),
    payload,
  );

export const verifyCurrentUser = async (payload): IApiPromise<any> =>
  await api.post(UserRoute.postEmailUserVerify, payload);

export const verifyUserCallback = (payload): IApiPromise<any> =>
  api.post(
    UserRoute.postEmailUserVerifyCallback,
    {
      secret: payload.secretKey,
      token: payload.token,
    },
    {
      params: { "g-recaptcha-response": payload.recaptcha },
    } as any,
  );

export const getAccessRequest =
  async (): IApiPromise<IOrgAccessRequestResponse> =>
    await api.get(Invite.getOrgAccessRequest, {
      params: {
        type: "received",
        includeActioned: false,
        includeArchived: false,
      },
    });

export const getAccessOffer = async (): IApiPromise<IOrgAccessOfferResponse> =>
  await api.get(Invite.getOrgAccessOffers, {
    params: {
      type: "received",
      includeActioned: false,
      includeArchived: false,
    },
  });

export const restoreUser = async (userId: string): IApiPromise<any> =>
  await api.post(Org.restoreUser.replace(":userId", userId));

export const makeAsUnread = async ({ actionId }): IApiPromise<any> =>
  await api.put(UserRoute.putActionMakeAsUnread.replace(":actionId", actionId));

export const deleteNotification = async ({ actionId }): IApiPromise<any> =>
  await api.put(UserRoute.deleteNotification.replace(":actionId", actionId));

export const deleteAllNotification = async (): IApiPromise<any> =>
  await api.delete(UserRoute.deleteAllNotification);
